import moment from 'moment-timezone'

const SIGNED_AUTHORIZATIONS_BY_FIELD = Object.freeze({
  first_lunch_period_authorization: ['lunch_start'],
  early_lunch_end_authorization: ['lunch_start', 'lunch_end'],
  early_second_lunch_end_authorization: ['second_lunch_start', 'second_lunch_end'],
  no_lunch_authorization: ['lunch_start', 'lunch_end'],
  no_second_lunch_authorization: ['second_lunch_start', 'second_lunch_end'],
  late_start_authorization: ['start'],
  early_end_authorization: ['end'],
  early_start_authorization: ['start'],
  overtime_authorization: ['work_hours'],
  short_shift_authorization: ['work_hours'],
  late_end_authorization: ['end']
})

const emptyMetadata = () => ({
  authorizationKeys: [],
  messages: [],
  color: null
})

const getAuthorizationMessage = (authorization, authStatuses, authRules) => {
  const title = authStatuses?.[authorization]?.title ?? authorization
  const rule = authRules?.[authorization]

  return rule ? `${title}: ${rule}` : title
}

const getSignedDifference = (signedTime, trackedTime, discrepancyThreshold) => {
  const difference = Math.abs(moment(signedTime).diff(moment(trackedTime), 'minutes'))

  return difference > discrepancyThreshold ? difference : null
}

export const getShiftTimeHighlightMetadata = ({
  row,
  prefix,
  authorizationKeys = [],
  shift,
  authStatuses = {},
  authRules = {},
  resolveColor = () => null,
  discrepancyThreshold = 0
}) => {
  if (!row) return emptyMetadata()

  const activeKeys = authorizationKeys ?? []
  const messages = row.tooltip ? [row.tooltip] : []
  const matchedKeys = []

  if (prefix === 'scheduled_') {
    const authorization = 'unscheduled_shift_authorization'

    if (activeKeys.includes(authorization) && (['start', 'end'].includes(row.key) || row.highlightKeys?.some(key => ['start', 'end'].includes(key)))) {
      matchedKeys.push(authorization)
      messages.push(getAuthorizationMessage(authorization, authStatuses, authRules))
    }
  } else if (prefix === 'actual_') {
    const authorization = 'no_actual_time_authorization'

    if (activeKeys.includes(authorization) && (row.key === 'end' || row.highlightKeys?.includes('end'))) {
      matchedKeys.push(authorization)
      messages.push(getAuthorizationMessage(authorization, authStatuses, authRules))
    }
  } else {
    const discrepancyAuthorization = 'signed_actual_time_discrepancy'

    if (activeKeys.includes(discrepancyAuthorization) && shift) {
      const rowKeys = row.highlightKeys ?? [row.key]
      const isStart = rowKeys.includes('start')
      const isEnd = rowKeys.includes('end')
      const difference = isStart
        ? getSignedDifference(shift.signed_start, shift.actual_work_start, discrepancyThreshold)
        : isEnd
          ? getSignedDifference(shift.signed_end, shift.actual_work_end, discrepancyThreshold)
          : null

      if (difference !== null) {
        matchedKeys.push(discrepancyAuthorization)
        messages.push(
          `${authStatuses?.[discrepancyAuthorization]?.title ?? discrepancyAuthorization}: Signed ${row.label.toLowerCase()} time differs ${difference} minutes from tracked time`
        )
      }
    }

    if (!matchedKeys.includes(discrepancyAuthorization)) {
      Object.entries(SIGNED_AUTHORIZATIONS_BY_FIELD).forEach(([authorization, fields]) => {
        const rowKeys = row.highlightKeys ?? [row.key]

        if (activeKeys.includes(authorization) && rowKeys.some(rowKey => fields.includes(rowKey))) {
          matchedKeys.push(authorization)
          messages.push(getAuthorizationMessage(authorization, authStatuses, authRules))
        }
      })
    }
  }

  return {
    authorizationKeys: matchedKeys,
    messages,
    color: matchedKeys.length ? resolveColor(matchedKeys[0]) : null
  }
}
